"use client";

import { useEffect, useRef } from "react";

interface UseGooglePlacesOptions {
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
}

export function useGooglePlaces({ onPlaceSelect }: UseGooglePlacesOptions) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;
    if (!key) {
      console.warn(
        "useGooglePlaces: NEXT_PUBLIC_GOOGLE_PLACES_API_KEY is not set. Autocomplete disabled.",
      );
      return;
    }

    if (!inputRef.current) return;

    const input = inputRef.current;

    function initAutocomplete() {
      if (autocompleteRef.current) return;

      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["address_components", "formatted_address", "geometry"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place) {
          onPlaceSelect(place);
        }
      });

      autocompleteRef.current = autocomplete;
    }

    if (typeof google !== "undefined" && google.maps?.places) {
      initAutocomplete();
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );
    if (existingScript) {
      existingScript.addEventListener("load", initAutocomplete);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", initAutocomplete);
    document.head.appendChild(script);
  }, [onPlaceSelect]);

  return { inputRef };
}
